import React, { useState, useMemo, useEffect, useCallback } from 'react';
import moment from "moment";
import { useListCustomersTopLevel, useListCustomerProfiles, AcceptedCommodity, acceptedCommodityTypes, RiskCropProfile, RiskCropProfileHistory, RiskManagementCustomerProfile } from '../../lib/api/risk/profiles';
import { custom, z } from 'zod';
import {
  CashSalesFiltersValidation, RiskManagementCashSales, useListRiskCashSales, useListStagedCashSales,
  useProcessStagedRow, useDeleteStagedRow,
  useDeleteCashSaleRow
} from '@/lib/api/risk/cashSales';
import { CommonDestinationTimezone, GenerateLoadingOrError, genFlatOptionList, getUniqueStrings } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { BearDecimalInput, BearIntInput } from '@/lib/form/ui';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useDebounce from '../otc/useDebounce';
import { useSearchParams } from 'react-router-dom';
import '@/styles/riskCompactTable.css';
import { Badge } from "@/components/ui/badge";
import { X, ChevronDown, ChevronUp, DeleteIcon, Trash2Icon, ArrowDownIcon, ArrowRightIcon } from "lucide-react";
import { DataTable } from "@/components/ui/virtualizedTable";
import { ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronRight, Search, Plus, Edit } from "lucide-react";
import { CreateCropProfileDialog, EditCropProfileDialog } from './CropProfileForm';
import { useListCustomers } from '@/lib/api/customer';
import { cn } from '@/lib/utils';
import { formatCommodityToUom, formatNumberToDisplay, safeParseDecimal, sortOptionMonths } from '@/lib/api/common';
import { Separator } from '@/components/ui/separator';
import { Uom } from '@/lib/api/commodity';
import { RiskProfileDetailModal } from '@/components/modals/RiskProfileDetailModal';
import { CropProfileReportModal } from '@/section/risk/GenerateProfileReportModal';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SummaryCard from './SummaryCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RiskManagementSummaryTables from './SummaryTables';
import { useListTraders } from '@/lib/api/trader';
import { Trader } from '@/lib/api/reports';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { generateContractTypesWith } from '@/lib/api/generalContract';
import { UploadCashReportDialog } from './UploadCashReportDialog';
import { DatePicker } from '@/components/ui/typeable_date_picker';
import { useToast } from "@/components/ui/use-toast";
import { Label } from '@/components/ui/label';

type FiltersState = z.infer<typeof CashSalesFiltersValidation>;

interface SearchFilterState {
  searchTerm: string;
  showFilters: boolean;
}

export function RiskCashSalesDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setTab] = useState<string>(searchParams.get("tab") || 'summary');

  const setSpecificParam = (paramName: string, paramValue: string) => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set(paramName, paramValue);
      return newParams;
    });
  };

  const onTabChange = (tab: string) => {
    setTab(tab);
    setSpecificParam("tab", tab);
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Risk Cash Sales Dashboard</h1>
      <Tabs value={currentTab} onValueChange={(tab) => onTabChange(tab)} >
        <TabsList className="mb-4">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="staged">Staged</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <RiskCashSalesTopLevel />
        </TabsContent>
        <TabsContent value="staged">
          <RiskCashSalesStaged />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default RiskCashSalesDashboard;

type ExpandedState = Record<string, boolean>;

function RiskCashSalesStaged() {
  const [selectedStagedId, setSelectedStagedId] = useState<undefined | string>(undefined);
  const [isProcessDialogOpen, setIsProcessDialogOpen] = useState(false);
  const listStagedCashSalesResult = useListStagedCashSales();

  const cashSales = listStagedCashSalesResult?.data?.data?.staged_data || [];
  const matchedCashSalesByCompoundId = listStagedCashSalesResult?.data?.data?.matchedCashSalesByCompoundId || [];
  const processingCashSale = cashSales.find((cashSale: any) => cashSale.id === selectedStagedId);

  const loadingOrErrorUi = GenerateLoadingOrError([listStagedCashSalesResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const createOnClick = (id: string) => () => {
    setIsProcessDialogOpen(true);
    setSelectedStagedId(id);
  };

  const columns = [
    {
      id: "delete",
      accessorKey: 'delete',
      header: 'Delete',
      cell: ({ row }: any) => { return <ConfirmDeleteButton isStaged={true} cashSale={row.original} /> },
      enableColumnFilter: false,
    },
    {
      id: "customer",
      accessorKey: 'customer',
      header: 'Customer',
      cell: ({ row }: any) => { return row.original.Customer?.long_name; },
      enableColumnFilter: false,
    },
    {
      id: "contract_type",
      accessorKey: 'contract_type',
      header: 'Contract Type',
      cell: ({ row }: any) => { return row.original.contract_typ; },
      enableColumnFilter: false,
    },
    {
      id: "contract_number",
      accessorKey: 'contract_number',
      header: 'Contract #',
      cell: ({ row }: any) => { return row.original.contract_number; },
      enableColumnFilter: false,
    },
    {
      id: "release",
      accessorKey: 'release',
      header: 'Release',
      cell: ({ row }: any) => { return row.original.contract_release; },
      enableColumnFilter: false,
    },
    {
      id: "commodity",
      accessorKey: 'commodity',
      header: 'Commodity',
      cell: ({ row }: any) => { return row.original.commodity; },
      enableColumnFilter: false,
    },
    {
      id: "option_month",
      accessorKey: 'option_month',
      header: 'Option Month',
      cell: ({ row }: any) => { return row.original.option_month; },
      enableColumnFilter: false,
    },
    {
      id: "crop_year",
      accessorKey: 'crop_year',
      header: 'Crop Year',
      cell: ({ row }: any) => { return row.original.crop_year; },
      enableColumnFilter: false,
    },
    {
      id: "quantity",
      accessorKey: 'quantity',
      header: 'Quantity',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.quantity, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(row.original.commodity_type) }) },
      enableColumnFilter: false,
    },
    {
      id: "balance",
      accessorKey: 'balance',
      header: 'Balance',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.balance, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(row.original.commodity_type) }); },
      enableColumnFilter: false,
    },
    {
      id: "futures",
      accessorKey: 'futures',
      header: 'Futures',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.futures, { emptyFormat: "--", zeroFormat: "--" }); },
      enableColumnFilter: false,
    },
    {
      id: "basis",
      accessorKey: 'basis',
      header: 'Basis',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.basis, { emptyFormat: "--", zeroFormat: "--" }); },
      enableColumnFilter: false,
    },
    {
      id: "shipment_start",
      accessorKey: 'shipment_start',
      header: 'Shipment Start',
      cell: ({ row }: any) => { return row.original.start_date ? moment.tz(row.original.start_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "--" },
      enableColumnFilter: false,
    },
    {
      id: "shipment_end",
      accessorKey: 'shipment_end',
      header: 'Shipment End',
      cell: ({ row }: any) => { return row.original.end_date ? moment.tz(row.original.end_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "--"; },
      enableColumnFilter: false,
    },
    {
      id: "market_zone",
      accessorKey: 'market_zone',
      header: 'Market Zone',
      cell: ({ row }: any) => { return row.original.MarketZone?.dtn_market_zone || ""; },
      enableColumnFilter: false,
    },
    {
      id: "location",
      accessorKey: 'location',
      header: 'Location',
      cell: ({ row }: any) => { return row.original.Location?.long_name || ""; },
      enableColumnFilter: false,
    },
    {
      id: "trader",
      accessorKey: 'trader',
      header: 'Trader',
      cell: ({ row }: any) => { return row.original.Trader?.initials || ""; },
      enableColumnFilter: false,
    },
  ];

  return (
    <div>
      <DataTable
        defaultGrouping={["contract_number"]}
        height="600px"
        columns={columns}
        data={cashSales!}
        className={`mx-4`}
      />
      <ProcessStagedRowDialog
        isOpen={isProcessDialogOpen}
        setIsOpen={setIsProcessDialogOpen}
        cashSale={processingCashSale}
        matchedCashSalesByCompoundId={matchedCashSalesByCompoundId}
      />
    </div>
  );
}

function RiskCashSalesTopLevel({ }: {}) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [isEditing, setIsEditing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize filters from URL parameters
  const [filters, setFilters] = useState<FiltersState>(() => {
    const initialFilters: FiltersState = { is_enabled: true };
    searchParams.entries().forEach(([key, value]) => {
      (initialFilters as any)[key] = value;
    })
    initialFilters.is_enabled = true;

    return initialFilters;
  });

  const [searchFilter, setSearchFilter] = useState<SearchFilterState>({
    searchTerm: '',
    showFilters: true,
  });
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [searchTerms, setSearchTerms] = useState<string[]>(() => {
    // Initialize from URL search parameters if available
    return searchParams.getAll('search');
  });
  const debouncedFilters = useDebounce(filters, 300);

  // Prepare filters for API - convert array of crop years to single value the API expects
  const prepareApiFilters = (filters: FiltersState): z.infer<typeof CashSalesFiltersValidation> => {
    const apiFilters: z.infer<typeof CashSalesFiltersValidation> = {
      ...filters,
    };
    return apiFilters;
  };

  const baseTopLevelResult = useListRiskCashSales({ filters: { is_enabled: true } });
  const listCashSalesResult = useListRiskCashSales({ filters: prepareApiFilters(debouncedFilters) });

  const updateSearchParams = (newFilters: FiltersState) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete('contract_number');
    params.delete('commodity');
    params.delete('location_id');
    params.delete('option_month');
    params.delete('is_enabled');
    params.delete('contract_type');
    params.delete('customer_id');
    params.delete('trader_id');
    params.delete('market_zone_id');
    params.delete('crop_year');
    params.delete('range_start');
    params.delete('range_end');

    Object.keys(newFilters).forEach(key => {
      const value: string = (newFilters as any)[key];
      if (value) {
        params.set(key, value);
      }
    })

    params.set("is_enabled", "true");

    setSearchParams(params);
  };

  const handleFilterChange = <K extends keyof FiltersState>(key: K, value?: string) => {
    setFilters(prev => {
      const newFilters: any = { ...prev };
      delete newFilters[key];
      if (value) {
        newFilters[key] = value
      }

      updateSearchParams(newFilters);
      return newFilters;
    });
  };

  const handleRemoveFilter = <K extends keyof FiltersState>(key: K, value?: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];

      updateSearchParams(newFilters);
      return newFilters;
    });
  };

  const handleClearFilters = () => {
    setFilters({ is_enabled: true });
    setSearchParams({});
  };

  const handleBackToList = () => {
    setSearchParams({});
  };

  const cashSales = listCashSalesResult?.data?.data?.cash_sales || [];

  const { knownCropYearOptions, knownContractNumberOptions, cashSalesNumbers, cashSalesGroupByContractNumber, knownCustomerOptions, knownLocationOptions, knownTraderOptions, knownCommodityOptions, knownContractTypesOptions, knownMarketZoneOptions, knownOptionMonthOptions } = useMemo(() => {
    const base_cash_sales: RiskManagementCashSales[] = (listCashSalesResult?.data?.data?.cash_sales || []);

    const knownCustomersMap = base_cash_sales.reduce((acc: Record<string, any>, profile: RiskManagementCashSales) => {
      if (!!acc[profile.customer_id]) return acc;
      acc[profile.customer_id] = profile.Customer;
      return acc;
    }, {} as Record<string, any>);
    const knownCustomerOptions = Object.values(knownCustomersMap).map((customer: any) => ({ value: customer.id, label: customer.long_name })).sort((a, b) => a.label.localeCompare(b.label));

    const knownCommodities = getUniqueStrings(base_cash_sales.map(sale => sale.commodity)).sort();
    const knownContractNumbers = getUniqueStrings(base_cash_sales.map(sale => sale.contract_number)).sort((a, b) => b.localeCompare(a));
    const knownCropYears = getUniqueStrings(base_cash_sales.map(sale => sale.crop_year)).sort((a, b) => parseInt(b) - parseInt(a));
    const knownLocations = getUniqueStrings(base_cash_sales.map(sale => sale.location_id)).sort();
    const knownTraders = getUniqueStrings(base_cash_sales.map(sale => sale.trader_id)).sort();
    const knownMarketZones = getUniqueStrings(base_cash_sales.map(sale => sale.market_zone_id)).sort();
    const knownContractTypes = getUniqueStrings(base_cash_sales.map(sale => sale.contract_type)).sort();
    const knownOptionMonths = getUniqueStrings(base_cash_sales.map(sale => sale.option_month ? sale.option_month : "(Blanks)")).sort(sortOptionMonths);

    const knownCommodityOptions = knownCommodities.map((commodity) => ({ value: commodity, label: commodity }));
    const knownContractNumberOptions = knownContractNumbers.map((contract_number) => ({ value: contract_number, label: contract_number }));
    const knownCropYearOptions = knownCropYears.map((crop_year) => ({ value: crop_year.toString(), label: crop_year.toString() }));
    const knownOptionMonthOptions = knownOptionMonths.map((optionMonth) => ({ value: optionMonth, label: optionMonth }));
    const knownMarketZoneOptions = knownMarketZones.map((market_zone_id) => ({ value: market_zone_id, label: base_cash_sales.find(sale => sale.market_zone_id === market_zone_id)?.MarketZone?.dtn_market_zone || "" })).sort((a, b) => a.label.localeCompare(b.label));
    const knownLocationOptions = knownLocations.map((location_id) => ({ value: location_id, label: base_cash_sales.find(sale => sale.location_id === location_id)?.Location?.long_name || "" })).sort((a, b) => a.label.localeCompare(b.label));
    const knownTraderOptions = knownTraders.map((trader_id) => ({ value: trader_id, label: base_cash_sales.find(sale => sale.trader_id === trader_id)?.Trader?.initials || "" })).sort((a, b) => a.label.localeCompare(b.label));
    const knownContractTypesOptions = knownContractTypes.map((contract_type) => ({ value: contract_type, label: contract_type }))

    const cashSalesNumbers = knownContractNumbers.map((contract_number) => {
      const found = base_cash_sales.find(cashSale => cashSale.contract_number === contract_number)!;
      const options = genFlatOptionList(generateContractTypesWith({}));
      const display_contract_type = options.find(option => option.value === found.contract_type)?.label || found.contract_type;

      return ({ contract_number, Customer: found.Customer!, display_contract_type });
    });
    const cashSalesGroupByContractNumber = knownContractNumbers.reduce((acc, contract_number: string) => {
      const releases = base_cash_sales.filter(cashSale => cashSale.contract_number === contract_number)!.sort((a, b) => {
        const releaseA = parseInt(a.contract_release);
        const releaseB = parseInt(b.contract_release);
        return releaseA - releaseB;
      });

      acc[contract_number] = releases;
      return acc;
    }, {} as Record<string, RiskManagementCashSales[]>);

    return { knownCropYearOptions, knownContractNumberOptions, cashSalesGroupByContractNumber, cashSalesNumbers, knownCustomerOptions, knownCommodityOptions, knownContractTypesOptions, knownMarketZoneOptions, knownOptionMonthOptions, knownLocationOptions, knownTraderOptions };
  }, [
    listCashSalesResult.data?.data,
  ]);

  const loadingOrErrorUi = GenerateLoadingOrError([baseTopLevelResult, listCashSalesResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const toggleExpanded = (customerId: string) => {
    setExpanded(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const columns = [
    {
      id: 'contract_number',
      accessorKey: 'contract_number',
      header: ({ table }: any) => (
        <>
          <button
            {...{
              onClick: table.getToggleAllRowsExpandedHandler(),
            }}
          >
            {table.getIsAllRowsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
          </button>{' '}
          Contract Number
        </>
      ),
      cell: ({ row, getValue }: any) => (
        <div
          style={{
            // Since rows are flattened by default,
            // we can use the row.depth property
            // and paddingLeft to visually indicate the depth
            // of the row
            paddingLeft: `${row.depth * 2}rem`,
          }}
        >
          <div>
            {row.getCanExpand() ? (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: 'pointer' },
                }}
              >
                {row.getIsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
              </button>
            ) : (null)}{' '}
            {getValue()}
          </div>
        </div>
      ),
      footer: (props: any) => props.column.id,
    },
    {
      id: "contract_release",
      accessorKey: 'contract_release',
      header: 'Release',
      cell: ({ row }: any) => { return row.original.contract_release; },
    },
    {
      id: "delete",
      accessorKey: 'delete',
      header: 'Delete',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return <ConfirmDeleteButton isStaged={false} cashSale={row.original} /> },
    },
    {
      id: "customer",
      accessorKey: 'customer',
      header: 'Customer',
      cell: ({ row }: any) => { return row.original.Customer?.long_name || ""; },
      filterFn: 'customerName' as any,
      sortingFn: 'customerName' as any,
    },
    {
      id: "contract_type",
      accessorKey: 'contract_type',
      header: 'Contract Type',
      cell: ({ row }: any) => { return row.original.contract_type; },
    },
    {
      id: "commodity",
      accessorKey: 'commodity',
      header: 'Commodity',
      cell: ({ row }: any) => { return row.original.commodity; },
    },
    {
      id: "option_month",
      accessorKey: 'option_month',
      header: 'Option Month',
      cell: ({ row }: any) => { return row.original.option_month; },
    },
    {
      id: "crop_year",
      accessorKey: 'crop_year',
      header: 'Crop Year',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return row.original.crop_year; },
      aggregatedCell: ({ getValue }: any) => { return ""; },
    },
    {
      id: "quantity",
      accessorKey: 'quantity',
      header: 'Quantity',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.quantity, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(row.original.commodity_type) }) },
    },
    {
      id: "balance",
      accessorKey: 'balance',
      header: 'Balance',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.balance, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(row.original.commodity_type) }); },
    },
    {
      id: "futures",
      accessorKey: 'futures',
      header: 'Futures',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.futures, { emptyFormat: "--", zeroFormat: "--" }); },
    },
    {
      id: "basis",
      accessorKey: 'basis',
      header: 'Basis',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.basis, { emptyFormat: "--", zeroFormat: "--" }); },
    },
    {
      id: "shipment_start",
      accessorKey: 'shipment_start',
      header: 'Shipment Start',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return row.original.start_date ? moment.tz(row.original.start_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "--" },
    },
    {
      id: "shipment_end",
      accessorKey: 'shipment_end',
      header: 'Shipment End',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return row.original.end_date ? moment.tz(row.original.end_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "--"; },
    },
    {
      id: "market_zone",
      accessorKey: 'market_zone',
      header: 'Market Zone',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return row.original.MarketZone?.dtn_market_zone || ""; },
    },
    {
      id: "location",
      accessorKey: 'location',
      header: 'Location',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return row.original.Location?.long_name || ""; },
    },
    {
      id: "trader",
      accessorKey: 'trader',
      header: 'Trader',
      enableColumnFilter: false,
      cell: ({ row }: any) => { return row.original.Trader?.initials || ""; },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="">
        <div className="flex justify-between py-2">
          <h2 className="text-xl font-semibold mb-4">Cash Sales</h2>
          <UploadCashReportDialog />
        </div>
        <Collapsible.Root open={isFilterOpen} onOpenChange={setIsFilterOpen} className="p-3 border rounded-md space-y-3 mb-4">
          <div className="flex flex-row justify-between items-center">
            <Collapsible.Trigger asChild>
              <div className="flex items-center gap-2 cursor-pointer">
                <h2 className="text-xl font-semibold">Filters</h2>
                {isFilterOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
            </Collapsible.Trigger>
            <div className="flex gap-2">
              {!isFilterOpen && (
                <div className="flex flex-wrap gap-1 mr-4">
                  {filters.customer_id && (
                    <Badge variant="secondary" className="px-3 py-1">
                      {knownCustomerOptions.find(opt => opt.value === filters.customer_id)?.label || filters.customer_id}
                    </Badge>
                  )}
                  {filters.contract_type && (
                    <Badge variant="secondary" className="px-3 py-1">
                      Type: {knownContractTypesOptions.find(opt => opt.value === filters.contract_type)?.label || filters.contract_type}
                    </Badge>
                  )}
                  {filters.commodity && (
                    <Badge variant="secondary" className="px-3 py-1">
                      {filters.commodity}
                    </Badge>
                  )}
                  {filters.option_month && (
                    <Badge variant="secondary" className="px-3 py-1">
                      {filters.option_month}
                    </Badge>
                  )}
                  {filters.market_zone_id && (
                    <Badge variant="secondary" className="px-3 py-1">
                      Market Zone: {knownMarketZoneOptions.find(opt => opt.value === filters.market_zone_id)?.label || filters.market_zone_id}
                    </Badge>
                  )}
                  {filters.trader_id && (
                    <Badge variant="secondary" className="px-3 py-1">
                      {knownTraderOptions.find(opt => opt.value === filters.trader_id)?.label || filters.trader_id}
                    </Badge>
                  )}
                  {filters.range_start && (
                    <Badge variant="secondary" className="px-3 py-1">
                      From: {moment(filters.range_start).format("MM/DD/YYYY")}
                    </Badge>
                  )}
                  {filters.range_end && (
                    <Badge variant="secondary" className="px-3 py-1">
                      To: {moment(filters.range_end).format("MM/DD/YYYY")}
                    </Badge>
                  )}
                  {filters.location_id && (
                    <Badge variant="secondary" className="px-3 py-1">
                      {knownLocationOptions.find(opt => opt.value === filters.location_id)?.label || filters.location_id}
                    </Badge>
                  )}
                  {filters.contract_number && (
                    <Badge variant="secondary" className="px-3 py-1">
                      {filters.contract_number}
                    </Badge>
                  )}
                  {filters.crop_year && (
                    <Badge variant="secondary" className="px-3 py-1">
                      {filters.crop_year}
                    </Badge>
                  )}
                </div>
              )}
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>

          <Collapsible.Content>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pt-3">
              <div className="col-span-1">
                <div className="relative">
                  <label htmlFor="customerFilter" className="block text-sm font-medium text-gray-700">Customer</label>
                  <Combobox
                    id="customerFilter"
                    dwimOnChange
                    virtualizeList
                    options={knownCustomerOptions}
                    value={filters.customer_id || ""}
                    onValueChange={(val) => handleFilterChange("customer_id", val)}
                    placeholder="Select Customer"
                    className="w-full"
                    popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                  />
                </div>
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="contractTypeFilter" className="block text-sm font-medium text-gray-700">Contract Type</label>
                <Combobox
                  id='contractTypeFilter'
                  dwimOnChange
                  options={knownContractTypesOptions}
                  value={filters.contract_type || ""}
                  onValueChange={(val) => handleFilterChange("contract_type", val)}
                  placeholder="Select Contract Type"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="commodityFilter" className="block text-sm font-medium text-gray-700">Commodity</label>
                <Combobox
                  id="commodityFilter"
                  dwimOnChange
                  options={knownCommodityOptions}
                  value={filters.commodity || ""}
                  onValueChange={(val) => handleFilterChange("commodity", val)}
                  placeholder="Select Commodity"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="optionMonthFilter" className="block text-sm font-medium text-gray-700">Option Month</label>
                <Combobox
                  id="optionMonthFilter"
                  dwimOnChange
                  options={knownOptionMonthOptions}
                  value={filters.option_month || ""}
                  onValueChange={(val) => handleFilterChange("option_month", val)}
                  placeholder="Select Option Month"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="marketZoneFilter" className="block text-sm font-medium text-gray-700">Market Zone</label>
                <Combobox
                  id="marketZoneFilter"
                  dwimOnChange
                  options={knownMarketZoneOptions}
                  value={filters.market_zone_id || ""}
                  onValueChange={(val) => handleFilterChange("market_zone_id", val)}
                  placeholder="Select Market Zone"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="traderFilter" className="block text-sm font-medium text-gray-700">Trader</label>
                <Combobox
                  id="traderFilter"
                  dwimOnChange
                  options={knownTraderOptions}
                  value={filters.trader_id || ""}
                  onValueChange={(val) => handleFilterChange("trader_id", val)}
                  placeholder="Select trader"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="rangeStartFilter" className="block text-sm font-medium text-gray-700">Shipment Start</label>
                <DatePicker
                  id="rangeStartFilter"
                  value={filters.range_start ? moment(filters.range_start).toDate() : undefined}
                  onChange={(date) => handleFilterChange("range_start", date ? moment(date).format("YYYY-MM-DD") : undefined)}
                  placeholder="MM/DD/YYYY"
                  className=""
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="rangeEndFilter" className="block text-sm font-medium text-gray-700">Shipment End</label>
                <DatePicker
                  id="rangeEndFilter"
                  value={filters.range_end ? moment(filters.range_end).toDate() : undefined}
                  onChange={(date) => handleFilterChange("range_end", date ? moment(date).format("YYYY-MM-DD") : undefined)}
                  placeholder="MM/DD/YYYY"
                  className=""
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="locationFilter" className="block text-sm font-medium text-gray-700">Location</label>
                <Combobox
                  id="locationFilter"
                  dwimOnChange
                  options={knownLocationOptions}
                  value={filters.location_id || ""}
                  onValueChange={(val) => handleFilterChange("location_id", val)}
                  placeholder="Select location"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="contractNoFilter" className="block text-sm font-medium text-gray-700">Contract #</label>
                <Combobox
                  id="contractNoFilter"
                  dwimOnChange
                  options={knownContractNumberOptions}
                  value={filters.contract_number || ""}
                  onValueChange={(val) => handleFilterChange("contract_number", val)}
                  placeholder="Select Contract #"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              <div className="col-span-1 w-full">
                <label htmlFor="cropYearFilters" className="block text-sm font-medium text-gray-700">Crop Year</label>
                <Combobox
                  id="cropYearFilters"
                  dwimOnChange
                  options={knownCropYearOptions}
                  value={filters.crop_year?.toString() || ""}
                  onValueChange={(val) => handleFilterChange("crop_year", val)}
                  placeholder="Select Crop Year"
                  className="sm:min-w-[200px] md:w-full lg:w-full"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
            </div>
          </Collapsible.Content>
        </Collapsible.Root>
        <DataTable
          defaultGrouping={["contract_number"]}
          defaultSorting={[
            { id: "contract_number", desc: true },
            { id: "contract_release", desc: false },
            { id: "customer", desc: false },
          ]}
          height="600px"
          columns={columns}
          data={cashSales!}
          className={`mx-4`}
        />
      </div>
    </div>
  );
}

function ProcessStagedRowDialog({
  isOpen,
  setIsOpen,
  cashSale,
  matchedCashSalesByCompoundId,
}: {
  isOpen: boolean;
  setIsOpen: (item: boolean) => void;
  cashSale?: RiskManagementCashSales
  matchedCashSalesByCompoundId: RiskManagementCashSales[]
}) {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [existing_id, setExistingId] = useState<string | undefined>(undefined);
  const processStagedRow = useProcessStagedRow();

  useEffect(() => {
    if (!isOpen) { setExistingId(undefined); }
  }, [isOpen, setExistingId])

  const { knownReleaseOptions } = useMemo(() => {
    const knownReleaseOptions = matchedCashSalesByCompoundId.filter((item) => {
      return item.compound_id === cashSale?.compound_id;
    }).map((item) => {
      return {
        value: item.id,
        label: [
          item.contract_number,
          " - ",
          item.contract_release,
          ": ",
          formatNumberToDisplay(item.quantity, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(item.commodity_type) })
        ].join("")
      }
    });

    return { knownReleaseOptions };
  }, [cashSale || {}]);

  const onProcess = async (e: any) => {
    if (e?.preventDefault) { e.preventDefault(); }
    if (!cashSale?.id) { return; }
    if (isProcessing) { return; }
    setIsProcessing(true);

    const hasMatch = !!existing_id;

    try {
      const result = await processStagedRow({
        staged_id: cashSale.id,
        existing_id: existing_id ? existing_id : undefined,
      });

      if (!result.success) {
        throw result;
      }

      toast({
        title: hasMatch ? "Matched" : "Created"
      })
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to match staged data", error);
      toast({
        title: hasMatch ? "Failed to Match" : "Failed to create"
      })
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(status) => {
        if (!status) {
          setIsOpen(status);
        }
      }}
    >
      <DialogContent className="max-w-4xl lg:max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            Create or Match Row
          </DialogTitle>
        </DialogHeader>

        {cashSale && (
          <div className="space-y-2 flex flex-col lg:grid lg:grid-cols-3 w-full gap-4">
            <Card className="lg:col-span-2">
              <CardContent className="space-y-2 flex flex-col lg:grid lg:grid-cols-2 w-full">
                <div className="mt-4">
                  <Label className="font-semibold">Customer</Label>
                  <p>{cashSale.Customer?.long_name || ""}</p>
                </div>
                <div>
                  <Label className="font-semibold">Quantity</Label>
                  <p>{formatNumberToDisplay(cashSale.quantity, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(cashSale.commodity_type) })}</p>
                </div>
                <div>
                  <Label className="font-semibold">Contract Number</Label>
                  <p>{cashSale.contract_number}</p>
                </div>
                <div>
                  <Label className="font-semibold">Release Number</Label>
                  <p>{cashSale.contract_release}</p>
                </div>
              </CardContent>
            </Card>
            {!knownReleaseOptions.length && (
              <div className="relative">
                <p className="text-muted-foreground">No existing matches for Contract / Release</p>
              </div>
            )}
            {!!knownReleaseOptions.length && (
              <div className="relative">
                <label htmlFor="customerFilter" className="block text-sm font-medium text-gray-700">Match to Existing Release</label>
                <Combobox
                  id="customerFilter"
                  dwimOnChange
                  virtualizeList
                  disabled={!knownReleaseOptions.length}
                  options={knownReleaseOptions}
                  value={existing_id || ""}
                  onValueChange={(val) => setExistingId(val)}
                  placeholder="Select Earlier Cash Sale"
                  className="w-full"
                  popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                />
              </div>
            )}

          </div>
        )}

        <form className="flex flex-col gap-4" onSubmit={onProcess}>
          <Button type="submit" disabled={isProcessing}>{isProcessing ? "Processing..." : !!existing_id ? "Match" : "Create"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDeleteButton({ cashSale, isStaged }: { cashSale: RiskManagementCashSales; isStaged: boolean }) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const deleteStagedRow = useDeleteStagedRow();
  const deleteCashRow = useDeleteCashSaleRow();

  const onDelete = async (e: any) => {
    if (e?.preventDefault) { e.preventDefault(); }
    if (!cashSale.id) { return; }
    if (isDeleting) { return; }
    setIsDeleting(true);

    try {
      const result = isStaged ? await deleteStagedRow(cashSale.id) : await deleteCashRow(cashSale.id);
      if (!result.success) {
        throw result;
      }

      toast({
        title: "Deleted"
      })
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to delete cash sale data", error);
      toast({
        title: "Failed to delete"
      })
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(status) => {
        setIsOpen(status);
      }}
    >
      <DialogTrigger>
        <Trash2Icon className="w-4 h-4" />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to permanently Delete this?
          </DialogTitle>
        </DialogHeader>

        <div className="grid ">
          <Card>
            <CardContent className="space-y-2 flex flex-col lg:grid lg:grid-cols-2 w-full">
              <div className="mt-4">
                <Label className="font-semibold">Customer</Label>
                <p>{cashSale.Customer?.long_name || ""}</p>
              </div>
              <div>
                <Label className="font-semibold">Quantity</Label>
                <p>{formatNumberToDisplay(cashSale.quantity, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(cashSale.commodity_type) })}</p>
              </div>
              <div>
                <Label className="font-semibold">Contract Number</Label>
                <p>{cashSale.contract_number}</p>
              </div>
              <div>
                <Label className="font-semibold">Release Number</Label>
                <p>{cashSale.contract_release}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <form className="flex flex-col gap-4" onSubmit={onDelete}>
          <Button type="submit" disabled={isDeleting}>{isDeleting ? "Deleting..." : "Confirm"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
